import { useEffect, useRef, useCallback } from 'react';

export function useGameLoop(
  canvasRef: React.RefObject<HTMLCanvasElement>,
  update: (deltaTime: number) => void,
  render: (ctx: CanvasRenderingContext2D) => void,
  isPaused: boolean
) {
  const lastTimeRef = useRef<number>(0);
  const animationFrameRef = useRef<number>(0);

  const gameLoop = useCallback(
    (timestamp: number) => {
      const deltaTime = lastTimeRef.current
        ? (timestamp - lastTimeRef.current) / 1000
        : 0.016;
      lastTimeRef.current = timestamp;

      const canvas = canvasRef.current;
      if (!canvas) {
        animationFrameRef.current = requestAnimationFrame(gameLoop);
        return;
      }

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        animationFrameRef.current = requestAnimationFrame(gameLoop);
        return;
      }

      if (!isPaused) {
        update(Math.min(deltaTime, 0.05));
      }

      render(ctx);

      animationFrameRef.current = requestAnimationFrame(gameLoop);
    },
    [canvasRef, update, render, isPaused]
  );

  useEffect(() => {
    animationFrameRef.current = requestAnimationFrame(gameLoop);

    return () => {
      cancelAnimationFrame(animationFrameRef.current);
    };
  }, [gameLoop]);
}
